import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import Consultation from "@/components/sections/Consultation";
import SEOHead from "@/components/SEOHead";
import Breadcrumbs from "@/components/Breadcrumbs";
import KeyTakeaways from "@/components/sections/KeyTakeaways";
import ClinicalReviewNote from "@/components/ClinicalReviewNote";
import { RelatedPages } from "@/components/RelatedPages";
import {
  buildBreadcrumbList,
  CLINIC_REF,
  SITE_URL,
} from "@/components/schemaConstants";

const crumbs = [
  {
    name: "Research & Investigational Treatments",
    href: "/research-and-investigational-treatments",
  },
];

const ResearchAndInvestigationalPage: React.FC = () => {
  const faqs = [
    {
      q: "Does research on TMS for a condition mean it is FDA-cleared for that condition?",
      a: "No. Research findings and FDA clearance are separate things. A study may show promising results for a condition, but unless a device is specifically cleared or approved for that indication, clinical use is off-label. Off-label use is legal when a physician judges it appropriate, but the FDA has not reviewed it for that purpose and insurance typically does not cover it.",
    },
    {
      q: "What does 'off-label' mean?",
      a: "Off-label means a clinician uses an FDA-cleared device or FDA-approved drug for a condition or protocol not included in its FDA authorization. For example, using a TMS device cleared for depression to treat PTSD is off-label. Physicians may prescribe off-label when they judge the evidence and risks acceptable, but patients should be told explicitly and give informed consent.",
    },
    {
      q: "Is accelerated TMS the same as SAINT?",
      a: "No. SAINT® is one specific accelerated TMS protocol — 10 iTBS sessions per day for 5 days with fMRI targeting, delivered on the Magnus Neuromodulation System. It is the only accelerated TMS protocol with its own FDA clearance. Other accelerated schedules on conventional devices are off-label modifications.",
    },
    {
      q: "Can I participate in a clinical trial at Neuro Wellness?",
      a: "Neuro Wellness TMS Centers of America is a treatment provider, not a research institution. We do not conduct clinical trials. If you are interested in participating in research, ask your clinician about ongoing trials at academic medical centers, or search ClinicalTrials.gov.",
    },
    {
      q: "If a study shows TMS helped with a condition, should I try it?",
      a: "Discuss any research you have read with a qualified clinician. A study's results apply to the population studied, not necessarily to you. Your clinician can evaluate whether an FDA-cleared treatment, an off-label option, or a referral to a research study is the most appropriate next step for your situation.",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Research & Investigational Treatments | Neuro Wellness"
        description="Clear distinction between FDA-cleared TMS indications and emerging, investigational research. Understand what is established vs. experimental in neuromodulation."
        canonical="/research-and-investigational-treatments"
        keywords="FDA-cleared TMS, investigational TMS, off-label TMS, accelerated TMS research, SAINT FDA clearance, TMS for PTSD research, TMS for bipolar"
        schemaJson={[
          buildBreadcrumbList([{ name: "Home", href: "/" }, ...crumbs]),
          {
            "@context": "https://schema.org",
            "@type": "MedicalWebPage",
            name: "Research and Investigational Treatments in Neuromodulation",
            url: `${SITE_URL}/research-and-investigational-treatments`,
            inLanguage: "en-US",
            about: [
              {
                "@type": "MedicalTherapy",
                name: "Transcranial Magnetic Stimulation",
              },
              { "@type": "MedicalTherapy", name: "SAINT® Accelerated TMS" },
            ],
            publisher: CLINIC_REF,
            mainEntity: {
              "@type": "FAQPage",
              mainEntity: faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]}
      />

      <Navbar />
      <main className="pt-16 sm:pt-20">
        {/* Hero */}
        <section className="bg-gradient-hero py-10 sm:py-14">
          <div className="container-px mx-auto max-w-4xl">
            <Breadcrumbs crumbs={crumbs} className="mb-6" />
            <span className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-accent">
              Clarity & Transparency
            </span>
            <h1 className="mt-3 font-headings text-2xl font-extrabold leading-tight text-primary sm:text-3xl md:text-4xl">
              Research &amp; Investigational Treatments
            </h1>
            <p className="mt-4 max-w-3xl text-base text-foreground/80 leading-relaxed sm:text-lg">
              Some neuromodulation research explores conditions or protocols
              beyond currently FDA-cleared indications. Research findings do not
              automatically mean a treatment is FDA-cleared for that use. This
              page separates established, FDA-cleared indications from emerging
              and investigational research so patients and families can make
              informed decisions.
            </p>
            <div className="mt-4">
              <ClinicalReviewNote
                reviewedBy="Daniel Pietras, MD — Board-Certified Psychiatrist"
                lastUpdated="2026-11-03"
              />
            </div>
          </div>
        </section>

        {/* Key takeaways */}
        <KeyTakeaways
          items={[
            {
              text: "FDA clearance applies to specific devices, specific indications, and specific patient populations. A device cleared for depression is not automatically cleared for PTSD, OCD, or bipolar disorder.",
            },
            {
              text: "SAINT® is FDA-cleared for adults with major depressive disorder who have not improved on prior antidepressants. It is not FDA-cleared for bipolar depression, PTSD, OCD, or anxiety disorders alone.",
            },
            {
              text: "Conventional TMS is FDA-cleared for major depressive disorder, anxious depression (on certain devices), and OCD (on the BrainsWay H7 coil). TMS for PTSD or bipolar depression is off-label.",
            },
            {
              text: "SPRAVATO® (esketamine) is FDA-approved for treatment-resistant depression and for depressive symptoms with acute suicidal ideation. Other uses (PTSD, bipolar depression) are off-label.",
            },
            {
              text: "Research findings describe the populations studied. They do not guarantee individual outcomes or establish FDA clearance for a new indication.",
            },
          ]}
        />

        {/* FDA-cleared indications */}
        <section className="py-10 sm:py-12">
          <div className="container-px mx-auto max-w-4xl">
            <h2 className="font-headings text-xl font-bold text-primary sm:text-2xl">
              FDA-Cleared or FDA-Approved Indications
            </h2>
            <p className="mt-3 text-base text-foreground/80 leading-relaxed">
              These are the treatments Neuro Wellness offers within their
              FDA-authorized indications. A clinician determines individual
              eligibility — FDA clearance does not mean every patient is
              eligible.
            </p>

            <div className="mt-6 overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b-2 border-border">
                    <th className="py-3 pr-4 text-left font-bold text-primary">
                      Treatment
                    </th>
                    <th className="py-3 pr-4 text-left font-bold text-primary">
                      FDA Status
                    </th>
                    <th className="py-3 text-left font-bold text-primary">
                      Cleared/Approved For
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  <tr>
                    <td className="py-3 pr-4 font-medium text-foreground">
                      SAINT® (Magnus System)
                    </td>
                    <td className="py-3 pr-4 text-foreground/85">
                      FDA-cleared (510(k) K220177, 2022)
                    </td>
                    <td className="py-3 text-foreground/85">
                      Adults with MDD who have not improved on prior
                      antidepressants in the current episode
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 pr-4 font-medium text-foreground">
                      Conventional TMS (NeuroStar, BrainsWay, etc.)
                    </td>
                    <td className="py-3 pr-4 text-foreground/85">
                      FDA-cleared (first device 2008)
                    </td>
                    <td className="py-3 text-foreground/85">
                      MDD in adults; anxious depression (certain devices); OCD
                      (BrainsWay H7 coil); adolescents 15–21 for depression
                      (NeuroStar, 2024)
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 pr-4 font-medium text-foreground">
                      SPRAVATO® (esketamine)
                    </td>
                    <td className="py-3 pr-4 text-foreground/85">
                      FDA-approved (2019; monotherapy 2025)
                    </td>
                    <td className="py-3 text-foreground/85">
                      Treatment-resistant depression; depressive symptoms with
                      acute suicidal ideation (with oral antidepressant)
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Emerging research */}
        <section className="py-10 sm:py-12 bg-muted/20 border-y border-border">
          <div className="container-px mx-auto max-w-4xl">
            <h2 className="font-headings text-xl font-bold text-primary sm:text-2xl">
              Emerging Research (Not FDA-Cleared for These Uses)
            </h2>
            <p className="mt-3 text-base text-foreground/80 leading-relaxed">
              The following areas have published research but do not have
              FDA-cleared device indications. Clinical use for these conditions
              would be off-label. Research findings do not establish clinical
              availability or FDA clearance.
            </p>

            <div className="mt-6 space-y-4">
              <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-soft">
                <h3 className="font-headings text-base font-bold text-primary">
                  TMS for PTSD
                </h3>
                <p className="mt-2 text-sm text-foreground/85 leading-relaxed">
                  Several small randomized trials, including research within the
                  Department of Veterans Affairs, have tested TMS for PTSD with
                  different targets and frequencies. Some showed benefit over
                  sham; others did not. No TMS device is FDA-cleared for PTSD.
                  Trauma-focused psychotherapies (EMDR, prolonged exposure,
                  cognitive processing therapy) remain first-line treatments.
                </p>
              </div>

              <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-soft">
                <h3 className="font-headings text-base font-bold text-primary">
                  TMS for Bipolar Depression
                </h3>
                <p className="mt-2 text-sm text-foreground/85 leading-relaxed">
                  Randomized trials of TMS for the depressive phase of bipolar
                  disorder have had mixed results and small samples. No device
                  is FDA-cleared for bipolar disorder. TMS carries a small risk
                  of triggering mania or hypomania, so it should not be given
                  during a manic episode and requires close monitoring if used
                  off-label.
                </p>
              </div>

              <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-soft">
                <h3 className="font-headings text-base font-bold text-primary">
                  Accelerated TMS Protocols (Non-SAINT)
                </h3>
                <p className="mt-2 text-sm text-foreground/85 leading-relaxed">
                  Some clinics deliver more than one TMS session per day on
                  conventional devices and describe this as accelerated
                  treatment. Only the Magnus Neuromodulation System with SAINT®
                  Technology is FDA-cleared as an accelerated protocol. Other
                  accelerated schedules are off-label modifications of cleared
                  devices, with smaller and more varied evidence.
                </p>
              </div>

              <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-soft">
                <h3 className="font-headings text-base font-bold text-primary">
                  TMS for Anxiety Disorders (Standalone)
                </h3>
                <p className="mt-2 text-sm text-foreground/85 leading-relaxed">
                  TMS is FDA-cleared for anxiety symptoms in adults who have
                  major depressive disorder (on certain devices). It is not
                  FDA-cleared for generalized anxiety disorder, panic disorder,
                  or social anxiety as standalone conditions. Small studies
                  exist but there is no pivotal trial.
                </p>
              </div>

              <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-soft">
                <h3 className="font-headings text-base font-bold text-primary">
                  Esketamine/Ketamine for Conditions Beyond TRD
                </h3>
                <p className="mt-2 text-sm text-foreground/85 leading-relaxed">
                  Some prescribers use SPRAVATO® or IV ketamine off-label for
                  bipolar depression, PTSD, or anxiety disorders. These uses are
                  not FDA-approved, the evidence is thinner than for
                  treatment-resistant depression, and insurance generally does
                  not cover them. IV ketamine is off-label for depression
                  entirely (it is FDA-approved only as an anesthetic).
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What this means for patients */}
        <section className="py-10 sm:py-12">
          <div className="container-px mx-auto max-w-4xl">
            <h2 className="font-headings text-xl font-bold text-primary sm:text-2xl">
              What This Means for Patients
            </h2>
            <div className="mt-4 space-y-4 text-base text-foreground/85 leading-relaxed">
              <p>
                When you read about a promising new use for TMS, esketamine, or
                another neuromodulation treatment, ask three questions: Is this
                use FDA-cleared or FDA-approved? What evidence supports it? Is
                it covered by insurance?
              </p>
              <p>
                FDA clearance means the FDA reviewed the device for a specific
                indication and found it safe and effective for that use.
                Research findings — even from peer-reviewed studies — do not
                automatically create FDA clearance. A treatment that works in a
                study population may not work for every individual.
              </p>
              <p>
                Neuro Wellness TMS Centers of America offers treatments within
                their FDA-authorized indications. If a treatment you are
                interested in is not FDA-cleared for your condition, we will
                tell you honestly and help you understand your options, which
                may include an FDA-cleared alternative or a referral to a
                research study.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-10 sm:py-12 bg-muted/20 border-y border-border">
          <div className="container-px mx-auto max-w-4xl">
            <h2 className="font-headings text-xl font-bold text-primary sm:text-2xl mb-5">
              Frequently Asked Questions
            </h2>
            <div className="space-y-3">
              {faqs.map((f, i) => (
                <details
                  key={i}
                  className="group rounded-xl border border-border bg-card p-5 shadow-soft"
                >
                  <summary className="flex cursor-pointer items-center justify-between font-headings text-base font-bold text-primary marker:content-['']">
                    {f.q}
                    <span
                      aria-hidden="true"
                      className="ml-3 shrink-0 text-accent transition-transform group-open:rotate-45"
                    >
                      +
                    </span>
                  </summary>
                  <p className="mt-3 text-base text-foreground/85 leading-relaxed">
                    {f.a}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Crisis notice */}
        <div className="container-px mx-auto max-w-4xl py-6">
          <p className="text-sm text-muted-foreground">
            If you are thinking about harming yourself, call or text{" "}
            <a
              href="tel:988"
              className="font-bold text-destructive underline underline-offset-2"
            >
              988
            </a>{" "}
            (Suicide &amp; Crisis Lifeline) in the U.S., or go to the nearest
            emergency room.
          </p>
        </div>

        <RelatedPages
          heading="Related Resources"
          links={[
            {
              title: "Clinical Research Library",
              href: "/clinical-research",
              description: "Peer-reviewed studies for SAINT, TMS, and SPRAVATO",
            },
            {
              title: "SAINT® TMS Treatment",
              href: "/saint",
              description: "FDA-cleared 5-day accelerated TMS",
            },
            {
              title: "Compare Treatments",
              href: "/compare-treatments",
              description: "Side-by-side treatment comparison",
            },
            {
              title: "Am I a Candidate?",
              href: "/am-i-a-candidate",
              description: "Eligibility screening factors",
            },
            {
              title: "TMS for Anxiety, OCD, PTSD",
              href: "/learn/tms-for-anxiety-ocd-ptsd",
              description: "FDA-cleared vs. off-label uses by condition",
            },
            {
              title: "TMS Devices Compared",
              href: "/learn/tms-devices-compared-saint-neurostar-brainsway-exomind",
              description: "SAINT, NeuroStar, BrainsWay, Exomind",
            },
          ]}
        />
        <Consultation />
      </main>
      <Footer />
    </div>
  );
};

export default ResearchAndInvestigationalPage;
